import { call, put, takeEvery, takeLatest, all, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { operatorsApi, CreateOperatorRequest, UpdateOperatorRequest } from '../../services/operatorsApi';
import {
  setLoading,
  setBulkLoading,
  setError,
  setOperators,
  setSelectedOperator,
  setSupervisors,
  addOperator,
  updateOperator,
  removeOperator,
  setDialogOpen,
  setValidationErrors,
  setStatistics,
  deselectAllOperators,
  FETCH_OPERATORS,
  FETCH_OPERATOR_BY_ID,
  CREATE_OPERATOR,
  UPDATE_OPERATOR,
  DELETE_OPERATOR,
  FETCH_SUPERVISORS,
  BULK_ACTIVATE_OPERATORS,
  BULK_DEACTIVATE_OPERATORS,
  FETCH_OPERATORS_STATISTICS,
} from '../reducers/operatorsSlice';
import { RootState } from '../index';

// Worker Sagas
function* fetchOperatorsSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const operators = yield call(operatorsApi.getAll);
    yield put(setOperators(operators));
  } catch (error: any) {
    console.error('Error fetching operators:', error);
    yield put(setError(error.message || 'Failed to fetch operators'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchOperatorByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const operator = yield call(operatorsApi.getById, action.payload);
    yield put(setSelectedOperator(operator));
  } catch (error: any) {
    console.error('Error fetching operator:', error);
    yield put(setError(error.message || 'Failed to fetch operator'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createOperatorSaga(action: PayloadAction<CreateOperatorRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the operator
    const validationErrors = yield call(operatorsApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newOperator = yield call(operatorsApi.create, action.payload);
    yield put(addOperator(newOperator));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    
    // Refresh statistics
    yield call(fetchOperatorsStatisticsSaga);
  } catch (error: any) {
    console.error('Error creating operator:', error);
    yield put(setError(error.message || 'Failed to create operator'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateOperatorSaga(action: PayloadAction<UpdateOperatorRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the operator
    const validationErrors = yield call(operatorsApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedOperator = yield call(operatorsApi.update, action.payload);
    yield put(updateOperator(updatedOperator));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    
    // Refresh statistics
    yield call(fetchOperatorsStatisticsSaga);
  } catch (error: any) {
    console.error('Error updating operator:', error);
    yield put(setError(error.message || 'Failed to update operator'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteOperatorSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    yield call(operatorsApi.delete, action.payload);
    yield put(removeOperator(action.payload));
    
    // Refresh statistics
    yield call(fetchOperatorsStatisticsSaga);
  } catch (error: any) {
    console.error('Error deleting operator:', error);
    yield put(setError(error.message || 'Failed to delete operator'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchSupervisorsSaga(): Generator<any, void, any> {
  try {
    const supervisors = yield call(operatorsApi.getSupervisors);
    yield put(setSupervisors(supervisors));
  } catch (error: any) {
    console.error('Error fetching supervisors:', error);
    yield put(setError(error.message || 'Failed to fetch supervisors'));
  }
}

function* bulkActivateOperatorsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(operatorsApi.bulkActivate, action.payload);
    yield put(deselectAllOperators());
    
    // Refresh the operators list
    yield call(fetchOperatorsSaga);
    yield call(fetchOperatorsStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk activating operators:', error);
    yield put(setError(error.message || 'Failed to activate operators'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* bulkDeactivateOperatorsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(operatorsApi.bulkDeactivate, action.payload);
    yield put(deselectAllOperators());
    
    // Refresh the operators list
    yield call(fetchOperatorsSaga);
    yield call(fetchOperatorsStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk deactivating operators:', error);
    yield put(setError(error.message || 'Failed to deactivate operators'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* fetchOperatorsStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(operatorsApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching operators statistics:', error);
    // Don't show error for statistics as it's not critical
  }
}

// Helper saga to auto-fetch supervisors when needed
function* autoFetchSupervisors(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    if (state.operators.supervisors.length === 0) {
      yield call(fetchSupervisorsSaga);
    }
  } catch (error) {
    // Ignore errors in auto-fetch
  }
}

// Watcher Sagas
function* watchFetchOperators(): Generator<any, void, any> {
  yield takeLatest(FETCH_OPERATORS, fetchOperatorsSaga);
}

function* watchFetchOperatorById(): Generator<any, void, any> {
  yield takeEvery(FETCH_OPERATOR_BY_ID, fetchOperatorByIdSaga);
}

function* watchCreateOperator(): Generator<any, void, any> {
  yield takeEvery(CREATE_OPERATOR, function*(action: PayloadAction<CreateOperatorRequest>): Generator<any, void, any> {
    yield call(autoFetchSupervisors);
    yield call(createOperatorSaga, action);
  });
}

function* watchUpdateOperator(): Generator<any, void, any> {
  yield takeEvery(UPDATE_OPERATOR, function*(action: PayloadAction<UpdateOperatorRequest>): Generator<any, void, any> {
    yield call(autoFetchSupervisors);
    yield call(updateOperatorSaga, action);
  });
}

function* watchDeleteOperator(): Generator<any, void, any> {
  yield takeEvery(DELETE_OPERATOR, deleteOperatorSaga);
}

function* watchFetchSupervisors(): Generator<any, void, any> {
  yield takeLatest(FETCH_SUPERVISORS, fetchSupervisorsSaga);
}

function* watchBulkActivateOperators(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE_OPERATORS, bulkActivateOperatorsSaga);
}

function* watchBulkDeactivateOperators(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE_OPERATORS, bulkDeactivateOperatorsSaga);
}

function* watchFetchOperatorsStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_OPERATORS_STATISTICS, fetchOperatorsStatisticsSaga);
}

// Root saga
export function* operatorsSaga(): Generator<any, void, any> {
  yield all([
    watchFetchOperators(),
    watchFetchOperatorById(),
    watchCreateOperator(),
    watchUpdateOperator(),
    watchDeleteOperator(),
    watchFetchSupervisors(),
    watchBulkActivateOperators(),
    watchBulkDeactivateOperators(),
    watchFetchOperatorsStatistics(),
  ]);
} 